import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { ProfileAvatar } from "./ProfileAvatar";
import { ProfileBanner } from "./ProfileBanner";
import { ProfileBio } from "./ProfileBio";
import { CombinedSkillsInterests } from "./ProfileSkillsInterests";
import { FollowersDialog } from "./FollowersDialog";
import { ProfileName } from "./ProfileName";
import { ProfileHeaderStats } from "./ProfileHeaderStats";
import { supabase } from "@/lib/supabase";
import { compressImage } from "@/utils/imageCompression";

export const ProfileHeaderEdit = () => {
  const { profile, updateProfile } = useProfile();
  const { user } = useAuth();
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [editedBio, setEditedBio] = useState(profile?.bio || "");
  const [newSkill, setNewSkill] = useState("");
  const [skills, setSkills] = useState<string[]>(profile?.skills || []);
  const [newInterest, setNewInterest] = useState("");
  const [interests, setInterests] = useState<string[]>(profile?.interests || []);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followingDialogOpen, setFollowingDialogOpen] = useState(false);

  const isOwnProfile = user?.id === profile?.id;

  const uploadImage = async (event: React.ChangeEvent<HTMLInputElement>, type: 'avatar' | 'banner') => {
    try {
      setUploading(true);
      
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const compressedFile = await compressImage(file);
      
      const fileExt = compressedFile.name.split('.').pop();
      const filePath = `${type}/${profile?.id}/${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profiles')
        .upload(filePath, compressedFile);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('profiles')
        .getPublicUrl(filePath);

      const updates = type === 'avatar' 
        ? { avatar_url: publicUrl }
        : { banner_url: publicUrl };

      await updateProfile.mutateAsync(updates);
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleNameSave = async (name: string) => {
    try {
      await updateProfile.mutateAsync({ full_name: name });
      toast({
        title: "Success",
        description: "Name updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleBioSave = async () => {
    try {
      await updateProfile.mutateAsync({ bio: editedBio });
      setIsEditingBio(false);
      toast({
        title: "Success",
        description: "Bio updated successfully",
      });
    } catch (error: any) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !skills.includes(newSkill.trim())) {
      const updatedSkills = [...skills, newSkill.trim()];
      setSkills(updatedSkills);
      updateProfile.mutateAsync({ skills: updatedSkills });
      setNewSkill("");
    }
  };

  const handleAddInterest = () => {
    if (newInterest.trim() && !interests.includes(newInterest.trim())) {
      const updatedInterests = [...interests, newInterest.trim()];
      setInterests(updatedInterests);
      updateProfile.mutateAsync({ interests: updatedInterests });
      setNewInterest("");
    }
  };

  const handleRemoveSkill = (skillToRemove: string) => {
    const updatedSkills = skills.filter(skill => skill !== skillToRemove);
    setSkills(updatedSkills);
    updateProfile.mutateAsync({ skills: updatedSkills });
  };

  const handleRemoveInterest = (interestToRemove: string) => {
    const updatedInterests = interests.filter(interest => interest !== interestToRemove);
    setInterests(updatedInterests);
    updateProfile.mutateAsync({ interests: updatedInterests });
  };

  if (!profile) return null;

  return (
    <>
      <div className="relative">
        <ProfileBanner
          bannerUrl={profile.banner_url}
          isOwnProfile={isOwnProfile}
          onUpload={(e) => uploadImage(e, 'banner')}
        />
        <div className="absolute -bottom-16 left-8">
          <ProfileAvatar
            profile={profile}
            isOwnProfile={isOwnProfile}
            onUpload={(e) => uploadImage(e, 'avatar')}
          />
        </div>
      </div>

      <div className="pt-20 flex flex-col md:flex-row md:justify-between md:items-start">
        <div>
          <ProfileName 
            fullName={profile.full_name}
            username={profile.username}
            isPro={profile.is_pro}
            isOwnProfile={isOwnProfile}
            onSave={handleNameSave}
            userId={profile.id}
          />
          <ProfileHeaderStats 
            profileId={profile.id}
            onFollowersClick={() => setFollowersDialogOpen(true)}
            onFollowingClick={() => setFollowingDialogOpen(true)}
          />
        </div>
        {!isOwnProfile && (
          <div className="flex gap-3 mt-4 md:mt-0">
            <Button variant="outline" className="border-flux-purple text-flux-purple hover:bg-flux-black bg-flux-black">
              Follow
            </Button>
            <Button variant="outline" className="border-flux-purple text-flux-purple hover:bg-flux-black bg-flux-black">
              <MessageSquare className="mr-2 h-4 w-4" />
              Message
            </Button>
          </div>
        )}
      </div>

      <div className="mt-8">
        <ProfileBio
          bio={profile.bio}
          isEditing={isEditingBio}
          isOwnProfile={isOwnProfile}
          editedBio={editedBio}
          onEdit={() => setIsEditingBio(true)}
          onChange={setEditedBio}
          onSave={handleBioSave}
        />

        <CombinedSkillsInterests
          skills={skills}
          interests={interests}
          isOwnProfile={isOwnProfile}
          newSkill={newSkill}
          newInterest={newInterest}
          onNewSkillChange={setNewSkill}
          onNewInterestChange={setNewInterest}
          onAddSkill={handleAddSkill}
          onAddInterest={handleAddInterest}
          onRemoveSkill={handleRemoveSkill}
          onRemoveInterest={handleRemoveInterest}
        />
      </div>

      <FollowersDialog
        isOpen={followersDialogOpen}
        onClose={() => setFollowersDialogOpen(false)}
        title="Followers"
        profileId={profile?.id || ''}
        type="followers"
      />
      <FollowersDialog
        isOpen={followingDialogOpen}
        onClose={() => setFollowingDialogOpen(false)}
        title="Following"
        profileId={profile?.id || ''}
        type="following"
      />
    </>
  );
};
